import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { type User } from '@/api/_schemas/users'
import { authService, LoginParams } from '@/services/auth'
import { getTokens, removeTokens } from '@/services/token-storage'

type AuthContextValues = {
  authenticated: boolean | null
  profile: User | null
  isLoading: boolean
  logout: () => void
  login: (params: LoginParams) => Promise<void>
}

type AuthContextProviderProps = PropsWithChildren

export const AuthContext = createContext<AuthContextValues>(
  {} as AuthContextValues,
)

const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const [auth, setAuth] = useState<Omit<AuthContextValues, 'login' | 'logout'>>(
    {
      authenticated: false,
      profile: null,
      isLoading: true,
    },
  )

  const isRun = useRef(false)

  useEffect(() => {
    if (isRun.current) return

    isRun.current = true

    const { tokenAgp, tokenBf } = getTokens()

    authService
      .login({
        tokenAgp,
        tokenBf,
      })
      .then((value) => setAuth(value))
  })

  return (
    <AuthContext.Provider
      value={{
        ...auth,
        login: (params) =>
          authService.login(params).then((value) => setAuth(value)),
        logout: () =>
          authService.logout(removeTokens).then((value) => setAuth(value)),
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

function useAuthContext() {
  return useContext(AuthContext)
}

export { AuthContextProvider, useAuthContext }
